// Lokale Daten eines Kontos für ein Deck: Bewertungen (SyncEngine aus platform/sync/sync.mjs) und alles, was keine Bewertung ist.
// Schlüssel im Speicher (userKey = Supabase-Nutzer-ID oder „lokal“ im Offline-Modus):
//   sentenzo.sync.<userKey>.<deck>.{log,outbox,cursor,lastSyncAt}   Bewertungen und Warteschlange (SyncEngine)
//   sentenzo.app.<userKey>.<deck>.state                               Ausblendungen, Meldungen, Einstufung, Sammlungen, Merker
//   sentenzo.app.<userKey>.user                                        Einstellungen (für alle Decks eines Kontos)
//   sentenzo.v2.<userKey>.<deck>.<pilot>.{log,outbox,cursor,lastSyncAt} Kartenablauf C1: eigenes Protokoll je Pilot (c1, a1), learning_events_v2
import { SyncEngine } from '../../platform/sync/sync.mjs';
import { readJson, writeJson } from './storage.js';
import { stableUuid } from './ids.js';

export const DEF_SETTINGS = () => ({ newPerDay: 5, dailyMinutes: 15, retention: 0.9, rate: 0.9 });
const DEF_STATE = () => ({
  v: 1,
  profile: { level: null, placement: null, done: false },   // Einstufung (Entscheidung 34); ältere Stände haben goal und test
  intro: {},                 // als neu gezeigt, noch nicht bewertet: { uid: Zeitpunkt }
  flags: {},                 // { uid: { known: true } | { fromCollection: slug } }
  quiet: [],                 // Ereignis-IDs, die nicht in Statistik und Lerntage eingehen (Schnell-Check, Sammlung)
  hidden: { s: {}, u: {} },  // ausgeblendete Sätze und Einheiten { id: Zeitpunkt }
  hiddenOps: [],             // noch nicht hochgeladen: [{ op: 'add'|'del', kind: 'sentence'|'unit', id, t }]
  reports: [],               // gemeldete Sätze [{ id, sid, reasons, text, t }]
  reportOutbox: [],          // IDs, die der Server noch nicht bestätigt hat
  collection: {},            // Fortschritt in Video-Sammlungen
  profileDirty: false,       // Einstufung noch nicht auf dem Server (deck_enrollments.placement)
  shiftDays: 0,              // Zeitreise (nur Entwicklung, nur Offline-Modus)
  pilot: null,               // Kartenablauf: null = bisheriger Ablauf (Entscheidung 13), 'c1' oder 'a1' (Entscheidung 21); je Einschreibung fest
  extraNew: null,            // „Noch mehr lernen“: { day, n } zusätzliche neue Wörter für diesen Lerntag (Tageseinstellung bleibt)
  noAudioDay: null,          // „Heute ohne Ton“: Lerntag, für den das Hören ausgesetzt ist
});
export const PILOTS = ['c1', 'a1'];
const V2_PREFIX = 'sentenzo.v2';

export class LocalDeck {
  constructor({ store, userKey, deckId, deviceId }) {
    Object.assign(this, { store, userKey, deckId, deviceId });
    this.engine = new SyncEngine({ store, remote: null, deviceId, userKey: `${userKey}.${deckId}` });
    this.key = `sentenzo.app.${userKey}.${deckId}.state`;
    this.userStateKey = `sentenzo.app.${userKey}.user`;
    this.state = Object.assign(DEF_STATE(), readJson(store, this.key, {}));
    this.user = Object.assign({ settings: DEF_SETTINGS(), settingsDirty: false }, readJson(store, this.userStateKey, {}));
    this.user.settings = Object.assign(DEF_SETTINGS(), this.user.settings);
  }
  save() { writeJson(this.store, this.key, this.state); }
  saveUser() { writeJson(this.store, this.userStateKey, this.user); }
  get events() { return this.engine.log; }
  // Protokoll des Kartenablaufs C1 für den Pilot dieser Einschreibung (sonst null)
  get v2() {
    return this.v2For(this.state.pilot);
  }
  v2For(p) {
    if (!PILOTS.includes(p)) return null;
    this._v2ByPilot ||= new Map();
    if (!this._v2ByPilot.has(p)) this._v2ByPilot.set(p,new SyncEngine({ store: this.store, remote: null, deviceId: this.deviceId, userKey: `${this.userKey}.${this.deckId}.${p}`, prefix: V2_PREFIX, timeField: 'occurred_at', chunk: 250 }));
    return this._v2ByPilot.get(p);
  }
  pending() { if(this.state.pilot)this.v2; return this.engine.outbox.length + [...(this._v2ByPilot?.values() || [])].reduce((n,e)=>n+e.outbox.length,0) + this.state.hiddenOps.length + this.state.reportOutbox.length + (this.state.profileDirty ? 1 : 0) + (this.user.settingsDirty ? 1 : 0); }

  record(e, quiet = false) {
    const ev = this.engine.record(Object.assign({ deck: this.deckId }, e));
    if (quiet) { this.state.quiet.push(ev.id); this.save(); }
    return ev;
  }
  hide(kind, id, t = Date.now()) {
    (kind === 'unit' ? this.state.hidden.u : this.state.hidden.s)[id] = t;
    this.state.hiddenOps.push({ op: 'add', kind, id, t }); this.save();
  }
  unhide(kind, id, t = Date.now()) {
    delete (kind === 'unit' ? this.state.hidden.u : this.state.hidden.s)[id];
    this.state.hiddenOps.push({ op: 'del', kind, id, t }); this.save();
  }
  report(r) { this.state.reports.push(r); this.state.reportOutbox.push(r.id); this.save(); }
}

// Alle Decks, für die ein Konto auf diesem Gerät Daten hat
export function decksOf(store, userKey) {
  const out = new Set();
  for (const k of store.keys(`sentenzo.sync.${userKey}.`)) out.add(k.slice(`sentenzo.sync.${userKey}.`.length).replace(/\.(log|outbox|cursor|lastSyncAt)$/, ''));
  for (const k of store.keys(`sentenzo.app.${userKey}.`)) { const m = k.slice(`sentenzo.app.${userKey}.`.length).match(/^(.+)\.state$/); if (m) out.add(m[1]); }
  return [...out];
}

export function removeUserData(store, userKey) {
  for (const k of [...store.keys(`sentenzo.sync.${userKey}.`), ...store.keys(`sentenzo.app.${userKey}.`), ...store.keys(`${V2_PREFIX}.${userKey}.`)]) store.remove(k);
}

// C1-Protokolle eines Kontos auf diesem Gerät: [{ deckId, pilot }]
export function v2LogsOf(store, userKey) {
  const out = new Map();
  for (const k of store.keys(`${V2_PREFIX}.${userKey}.`)) {
    const m = k.slice(`${V2_PREFIX}.${userKey}.`.length).match(/^(.+)\.(c1|a1)\.(log\.\d+|logc|outbox|cursor|lastSyncAt)$/);
    if (m) out.set(`${m[1]}|${m[2]}`, { deckId: m[1], pilot: m[2] });
  }
  return [...out.values()];
}

// Lokale Daten eines Kontos (anonym oder „lokal“) in ein anderes Konto übernehmen, z. B. wenn jemand anonym gelernt hat und sich dann mit einem
// bestehenden Konto anmeldet. Ereignisse bekommen neue, feste IDs: Die alten IDs können auf dem Server schon unter dem anonymen Konto stehen,
// dann würde der Server sie für das neue Konto als Dublette verwerfen. Feste IDs: Eine wiederholte Übernahme erzeugt keine Dubletten.
// Danach werden die Daten des alten Kontos auf diesem Gerät gelöscht (sie stehen vollständig im neuen Konto). Rückgabe: Zahl übernommener Ereignisse.
export function adoptUserData(store, fromKey, toKey, deviceId) {
  if (fromKey === toKey) return 0;
  let moved = 0;
  for (const deckId of decksOf(store, fromKey)) {
    const src = new LocalDeck({ store, userKey: fromKey, deckId, deviceId });
    const dst = new LocalDeck({ store, userKey: toKey, deckId, deviceId });
    const idMap = {};
    for (const e of src.engine.log) {
      const id = stableUuid(`adopt|${toKey}|${e.id}`);
      idMap[e.id] = id;
      if (dst.engine.ids.has(id)) continue;
      dst.engine.log.push(Object.assign({}, e, { id, deck: deckId })); dst.engine.ids.add(id); dst.engine.outbox.push(id); moved++;
    }
    dst.engine.persist();
    const S = src.state, T = dst.state;
    for (const q of S.quiet) if (idMap[q] && !T.quiet.includes(idMap[q])) T.quiet.push(idMap[q]);
    for (const [k, v] of Object.entries(S.intro)) if (!(k in T.intro)) T.intro[k] = v;
    for (const [k, v] of Object.entries(S.flags)) if (!(k in T.flags)) T.flags[k] = v;
    for (const kind of ['s', 'u']) for (const [id, t] of Object.entries(S.hidden[kind])) if (!(id in T.hidden[kind])) { T.hidden[kind][id] = t; T.hiddenOps.push({ op: 'add', kind: kind === 'u' ? 'unit' : 'sentence', id, t }); }
    const sentReports = new Set(T.reports.map(r => r.id));
    for (const r of S.reports) if (!sentReports.has(r.id)) { T.reports.push(r); if (S.reportOutbox.includes(r.id)) T.reportOutbox.push(r.id); }   // schon gesendete Meldungen stehen beim alten Konto auf dem Server
    for (const [slug, st] of Object.entries(S.collection)) if (!T.collection[slug]) T.collection[slug] = st;
    if (!T.profile.done && S.profile.done) { T.profile = S.profile; T.profileDirty = true; }
    if (!T.pilot && S.pilot) { T.pilot = S.pilot; T.profileDirty = true; }
    if (!T.noAudioDay && S.noAudioDay) T.noAudioDay = S.noAudioDay;
    if (!T.extraNew && S.extraNew) T.extraNew = S.extraNew;
    dst.save();
    if (!dst.user.settingsDirty && src.user.settingsDirty) { dst.user.settings = src.user.settings; dst.user.settingsDirty = true; dst.saveUser(); }
  }
  // Kartenablauf C1: Ereignisse mit neuen festen IDs übernehmen (Versuchs-IDs bleiben, damit Hilfen beim selben Versuch bleiben)
  for (const { deckId, pilot } of v2LogsOf(store, fromKey)) {
    const mk = key => new SyncEngine({ store, remote: null, deviceId, userKey: `${key}.${deckId}.${pilot}`, prefix: V2_PREFIX, timeField: 'occurred_at', chunk: 250 });
    const src = mk(fromKey), dst = mk(toKey);
    for (const e of src.log) { if (dst.add(Object.assign({}, e, { id: stableUuid(`adopt|${toKey}|${e.id}`) }))) moved++; }
  }
  removeUserData(store, fromKey);
  return moved;
}
